import { CalendarDateService } from '@uplift/services';
import React, { useMemo } from 'react';
import { useAllTimezonesQuery } from '@uplift/queries';
import { useCurrentuser, useSetTimezone } from '@uplift/contexts';
import { useUpdateUserTimezone } from '../../UserTimezoneSelect';
import { UserRole } from '@uplift/types';
import { useLocalStorageValue } from '@uplift/hooks';

export const TIMEZONE_WARNING_DISMISSED_KEY = 'timezoneWarningDismissed';
export const TIMEZONE_WARNING_IANA = 'America/Phoenix';

export type UseTimezoneWarning = {
  isActive: boolean;
  confirm: () => Promise<void>;
};

export const useTimezoneWarning = (): UseTimezoneWarning => {
  const me = useCurrentuser();
  const { data: timezones = [] } = useAllTimezonesQuery(UserRole.patient);
  const setTimezone = useSetTimezone();
  const updateUserTimezone = useUpdateUserTimezone();
  const [ dismissed, setDismissed ] = useLocalStorageValue<boolean>(TIMEZONE_WARNING_DISMISSED_KEY, false);
  const localIana = useMemo(() => CalendarDateService.date.tz.guess(), []);
  const localTimezone = useMemo(
    () => timezones.find(tz => tz.ianaName === localIana),
    [ timezones, localIana ]
  );

  const isActive = Boolean(
    localTimezone
    && localTimezone.ianaName === TIMEZONE_WARNING_IANA
    && !dismissed
  );

  const confirm = React.useCallback(async () => {
    if (!localTimezone) return;
    if (me.id) {
      await updateUserTimezone({ userId: me.id, timezone: localTimezone.ianaName });
    }
    setTimezone(localTimezone.ianaName);
    setDismissed(true);
  }, [ localTimezone, me.id, setTimezone, updateUserTimezone ]); // eslint-disable-line react-hooks/exhaustive-deps

  return { isActive, confirm };
};
